/**
 * Relationship tab — the Cedar-unique coaching relationship view.
 *
 * Layout:
 *   [ KPI strip: 7 novel coaching KPIs ]
 *   [ Tier donut (population-wide) | Anonymized per-user score table ]
 *   [ Context density leaderboard ]
 *   [ Recalibration timeline swimlanes ]
 */
import React, { useState } from 'react';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';
import { type AnalyticsRange } from '../hooks/useAnalyticsRange';
import type {
  ContextDensityLeaderboardPayload,
  RecalibrationTimelinePayload,
  RelationshipKpiStripPayload,
  RelationshipUsersPayload,
} from '../types';
import { ChartCard } from '../components/ChartCard';
import { KpiTile } from '../components/KpiTile';
import {
  RelationshipScoreTable,
  type RelationshipSortKey,
} from '../components/RelationshipScoreTable';
import { ContextDensityLeaderboard } from '../components/ContextDensityLeaderboard';
import { RecalibrationTimeline } from '../components/RecalibrationTimeline';
import { RelationshipTierDonut } from '../components/RelationshipTierDonut';
import { formatFloat, formatInt, formatPercent } from '../format';

export interface RelationshipTabProps {
  range: AnalyticsRange;
  revealed: boolean;
  isSuperAdmin: boolean;
}

export const RelationshipTab: React.FC<RelationshipTabProps> = ({
  range,
  revealed,
  isSuperAdmin,
}) => {
  const [sort, setSort] = useState<RelationshipSortKey>('relationship_score');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const revealQs = revealed && isSuperAdmin ? '&reveal=true' : '';

  const kpiStrip = useAnalyticsQuery<RelationshipKpiStripPayload>(
    ['relationship-kpi', range.queryString],
    `/api/admin/analytics/relationship/kpi-strip?${range.queryString}`,
  );
  const users = useAnalyticsQuery<RelationshipUsersPayload>(
    ['relationship-users', sort, page, revealQs],
    `/api/admin/analytics/relationship/users?sort=${sort}&page=${page}&page_size=${pageSize}${revealQs}`,
  );
  const density = useAnalyticsQuery<ContextDensityLeaderboardPayload>(
    ['context-density', revealQs],
    `/api/admin/analytics/relationship/leaderboard/context-density?limit=25${revealQs}`,
  );
  // Memory-recall heatmap REMOVED from the tab (page audit / Batch 5): the
  // `memory_recall` event is never emitted, so the panel was permanently
  // blank. Re-add when per-turn recall telemetry is instrumented; the backend
  // endpoint remains for that day.
  const timeline = useAnalyticsQuery<RecalibrationTimelinePayload>(
    ['recal-timeline', range.queryString, revealQs],
    `/api/admin/analytics/relationship/recalibrations/timeline?${range.queryString}&limit=10${revealQs}`,
  );

  const kpiData = kpiStrip.data?.data;
  // Population-wide tier mix from the backend (latest snapshot). The old
  // client-side version counted only the visible 25-row page, so the donut
  // changed as you paginated — sorted page 1 read all-"power".
  const tierBreakdown = users.data?.data.tier_breakdown ?? [];

  return (
    <div className="space-y-6">
      {/* KPI strip — the Cedar-unique signals */}
      <section>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Cedar coaching signals — {range.label}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
          {/* Two dead-denominator RATE tiles removed (recon 2026-07-04): their
              denominators (nudge subtype mismatch; the debrief-offered beacon) are
              never populated → always N/A. Debrief/weekly COMPLETION counts are real
              now — see the check-ins tile below (sourced from touchpoint_sessions /
              post_workout_analysis). */}
          <KpiTile
            label="Recalibrations / user / week"
            value={formatFloat(kpiData?.recalibrations_per_active_user_per_week, 2)}
            hint="life happens — buy-in signal"
            loading={kpiStrip.isLoading}
          />
          <KpiTile
            label="Weekly check-ins"
            value={formatInt(kpiData?.weekly_checkins_completed)}
            loading={kpiStrip.isLoading}
          />
          <KpiTile
            label='Avg "Chat" CTAs (Pro)'
            value={formatFloat(kpiData?.avg_cta_clicks_per_chat_plus_pro_user, 2)}
            loading={kpiStrip.isLoading}
          />
          <KpiTile
            label="Chat msgs / active / week"
            value={formatFloat(kpiData?.median_chat_messages_per_active_user_per_week, 1)}
            loading={kpiStrip.isLoading}
          />
          <KpiTile
            label="Weekly quota hits"
            value={formatInt(kpiData?.weekly_quota_hits)}
            loading={kpiStrip.isLoading}
          />
        </div>
      </section>

      {/* Tier donut + score table */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ChartCard
          title="Tier breakdown"
          subtitle="All scored users · latest snapshot"
          loading={users.isLoading}
          error={users.error?.message ?? null}
          isEmpty={tierBreakdown.length === 0}
          onRetry={() => users.refetch()}
          className="lg:col-span-1"
        >
          <RelationshipTierDonut data={tierBreakdown} />
        </ChartCard>

        <ChartCard
          title="Per-user relationship scores"
          subtitle="Anonymized, sortable, paginated"
          loading={users.isLoading}
          error={users.error?.message ?? null}
          onRetry={() => users.refetch()}
          className="lg:col-span-2"
          isEmpty={false}
        >
          <RelationshipScoreTable
            rows={users.data?.data.users ?? []}
            sort={sort}
            onSortChange={(k) => {
              setSort(k);
              setPage(1);
            }}
            page={page}
            pageSize={pageSize}
            total={users.data?.data.total ?? 0}
            onPageChange={setPage}
            loading={users.isLoading}
            revealed={users.data?.data.revealed ?? false}
          />
        </ChartCard>
      </div>

      {/* Context density */}
      <ChartCard
        title="Context density leaderboard"
        subtitle="Top users by stored memories + notes + research + health data"
        loading={density.isLoading}
        error={density.error?.message ?? null}
        isEmpty={!density.data?.data.leaderboard?.length}
        onRetry={() => density.refetch()}
      >
        <ContextDensityLeaderboard rows={density.data?.data.leaderboard ?? []} />
      </ChartCard>

      {/* Recalibration timeline — caption reflects the actual selected range
          (the old caption was a fixed window string over range-scoped data,
          audit F13) */}
      <ChartCard
        title="Recalibration timeline"
        subtitle={`Top 10 users by recalibration volume · ${range.label}`}
        loading={timeline.isLoading}
        error={timeline.error?.message ?? null}
        isEmpty={!timeline.data?.data.swimlanes?.length}
        onRetry={() => timeline.refetch()}
      >
        <RecalibrationTimeline
          swimlanes={timeline.data?.data.swimlanes ?? []}
          days={timeline.data?.data.days ?? 30}
        />
      </ChartCard>
    </div>
  );
};
